import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { formatCurrency } from '../utils/format';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { TrendIcon } from '../components/Icons';

/**
 * Dashboard page showing a compact grid of 8 data widgets.
 * Row 1: 4 stat cards (Total Spend, Invoices, Avg Invoice, Active Vendors).
 * Row 2: Price Watch + Top Vendors (2 columns).
 * Row 3: Recent Invoices + Spend by Category (2 columns).
 *
 * Always shows current month vs prior month. No filters, no charts.
 *
 * @param {Object} props
 * @param {string|number} [props.activeBusiness] - Currently selected business ID
 * @returns {JSX.Element}
 */
function Dashboard({ activeBusiness }) {
  const [summary, setSummary] = useState({
    total_spend: 0,
    invoice_count: 0,
    vendor_count: 0,
    avg_invoice: 0
  });
  const [percentChange, setPercentChange] = useState(null);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  /**
   * Load all dashboard data in parallel: summary stats, price alerts,
   * vendor breakdown, recent invoices, and missing invoice detection.
   *
   * @param {AbortSignal} [signal] - Signal for aborting requests
   */
  const loadDashboard = useCallback(async (signal) => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (activeBusiness) params.business_id = activeBusiness;

      const [summaryRes, priceRes, vendorRes, invoiceRes, categoryRes] = await Promise.all([
        api.getAnalyticsSummary({ ...params, comparison: 'mom' }),
        api.getPriceChanges({ ...params, threshold: 5, lookback_days: 30 }),
        api.getAnalyticsByVendor(params),
        api.getInvoices(params),
        api.getAnalyticsByCategory(params),
      ]);

      if (signal?.aborted) return;

      const sData = summaryRes.data;
      setSummary({
        total_spend: sData.total_spend || 0,
        invoice_count: sData.invoice_count || 0,
        vendor_count: sData.vendor_count || 0,
        avg_invoice: sData.avg_invoice || 0
      });
      setPercentChange(sData.percent_change || null);

      setPriceAlerts((priceRes.data || []).slice(0, 5));

      const vendors = vendorRes.data || [];
      const totalSpend = vendors.reduce((s, v) => s + (v.total_spent || 0), 0);
      setTopVendors(
        vendors.slice(0, 5).map(v => ({
          name: v.name,
          total_spent: v.total_spent || 0,
          pct: totalSpend > 0 ? ((v.total_spent || 0) / totalSpend * 100) : 0
        }))
      );

      const invoices = invoiceRes.data || [];
      const sorted = [...invoices].sort((a, b) =>
        new Date(b.invoice_date) - new Date(a.invoice_date)
      );
      setRecentInvoices(sorted.slice(0, 5));

      const categories = categoryRes.data || [];
      const catTotal = categories.reduce((s, c) => s + (c.total_spent || 0), 0);
      setTopCategories(
        categories.slice(0, 5).map(c => ({
          category: c.category,
          total_spent: c.total_spent || 0,
          pct: catTotal > 0 ? ((c.total_spent || 0) / catTotal * 100) : 0
        }))
      );

      setIsLoading(false);
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Error loading dashboard:', error);
      setLoadError('Failed to load dashboard data');
      setIsLoading(false);
    }
  }, [activeBusiness]);

  /**
   * Detect vendors with potentially missing invoices by comparing
   * current gap since last invoice to 2x their average gap.
   *
   * @param {Array} invoices - All invoices to analyze
   */


  useEffect(() => {
    const controller = new AbortController();
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, [loadDashboard]);

  /**
   * Render a change indicator with arrow and percentage.
   * Green for decreases (saving money), red for increases (spending more).
   *
   * @param {number|null} change - Percentage change value
   * @param {boolean} [invertColor=false] - If true, decrease is green (good for costs)
   * @returns {JSX.Element|null}
   */
  const renderChange = (change, invertColor = false) => {
    if (change == null || isNaN(change) || change === 0) return null;
    const isPositive = change > 0;
    const isGood = invertColor ? !isPositive : isPositive;
    const cls = isGood
      ? 'stat-card__change stat-card__change--positive'
      : 'stat-card__change stat-card__change--negative';
    return (
      <span className={cls}>
        <TrendIcon direction={isPositive ? 'up' : 'down'} /> {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <div className="skeleton-stats-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-stat-card">
              <div className="skeleton-bar"></div>
              <div className="skeleton-bar"></div>
            </div>
          ))}
        </div>
        <Loading variant="spinner" message="Loading dashboard data..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <div className="load-error">
          <p className="load-error__message">{loadError}</p>
          <button className="load-error__retry" onClick={() => loadDashboard()}>Retry</button>
        </div>
      </div>
    );
  }

  const isEmpty = !summary.invoice_count && topVendors.length === 0;

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Your COGS overview for this month</p>
      </div>

      {/* Empty State */}
      {isEmpty ? (
        <div className="card dashboard__empty-state">
          <div className="dashboard__empty-icon" aria-hidden="true">&#128202;</div>
          <h2 className="dashboard__empty-text">No Invoice Data Yet</h2>
          <p className="dashboard__empty-desc">
            Upload your first invoice to start tracking costs, vendor spending, and category breakdowns.
          </p>
          <div className="dashboard__empty-actions">
            <Link to="../ocr"><button type="button" className="btn btn-primary">Upload Invoice PDF</button></Link>
            <Link to="../invoice-entry"><button type="button" className="btn btn-secondary">Enter Invoice Manually</button></Link>
          </div>
        </div>
      ) : (
        <>
          {/* Row 1: 4 Stat Cards */}
          <div className="dashboard__grid">
            <div className="stat-card">
              <span className="stat-card__label">Total Spend</span>
              <span className="stat-card__value stat-card__value--money">{formatCurrency(summary.total_spend)}</span>
              {renderChange(percentChange?.total_spend, true)}
            </div>
            <div className="stat-card">
              <span className="stat-card__label">Invoices</span>
              <span className="stat-card__value">{summary.invoice_count}</span>
              {renderChange(percentChange?.invoice_count)}
            </div>
            <div className="stat-card">
              <span className="stat-card__label">Avg Invoice</span>
              <span className="stat-card__value stat-card__value--money">{formatCurrency(summary.avg_invoice)}</span>
              {renderChange(percentChange?.avg_invoice, true)}
            </div>
            <div className="stat-card">
              <span className="stat-card__label">Active Vendors</span>
              <span className="stat-card__value">{summary.vendor_count}</span>
              {renderChange(percentChange?.vendor_count)}
            </div>
          </div>

          {/* Row 2: Price Watch + Top Vendors */}
          <div className="dashboard__widgets">
            {/* Widget 5: Price Watch */}
            <div className="card">
              <h3 className="dashboard__widget-title">Price Watch</h3>
              {priceAlerts.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon" aria-hidden="true">&#9989;</span>
                  <span className="empty-state__title">No price increases &gt;5%</span>
                  <span className="empty-state__description">Prices are stable in the last 30 days.</span>
                </div>
              ) : (
                <ul className="dashboard__widget-list">
                  {priceAlerts.map((alert, i) => {
                    // GH #185: Ruth UAT — API returns
                    // new_normalized_price / old_normalized_price (Phase 11.1
                    // surface). Prior code read alert.old_price /
                    // alert.new_price which were undefined, so the widget
                    // rendered "$0.00 → $0.00" alongside a real change_pct.
                    // Fall back to legacy raw-price fields if the backend
                    // ever serves an older shape (forward-compat).
                    const pctChange = alert.change_pct || alert.percent_change || 0;
                    const oldPrice = alert.old_normalized_price ?? alert.old_price ?? 0;
                    const newPrice = alert.new_normalized_price ?? alert.new_price ?? 0;
                    return (
                      <li key={i} className="dashboard__widget-item">
                        <span className="dashboard__widget-item-name">{alert.item_name}</span>
                        <span className="dashboard__widget-item-detail">
                          <span className="td-amt">{formatCurrency(oldPrice)}</span>
                          <span className="dashboard__widget-arrow" aria-hidden="true">&rarr;</span>
                          <span className="td-amt">{formatCurrency(newPrice)}</span>
                          <span className="price-change--increase">
                            +{Math.abs(pctChange).toFixed(1)}%
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Link to="../analytics" className="dashboard__widget-link">View all in Analytics</Link>
            </div>

            {/* Widget 6: Top Vendors */}
            <div className="card">
              <h3 className="dashboard__widget-title">Top Vendors</h3>
              {topVendors.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon" aria-hidden="true">&#127978;</span>
                  <span className="empty-state__title">No vendor data</span>
                </div>
              ) : (
                <ol className="dashboard__widget-list dashboard__widget-list--ranked">
                  {topVendors.map((v, i) => (
                    <li key={i} className="dashboard__widget-item">
                      <span className="dashboard__widget-item-rank">{i + 1}</span>
                      <span className="dashboard__widget-item-name">{v.name}</span>
                      <span className="dashboard__widget-item-detail">
                        <span className="td-amt">{formatCurrency(v.total_spent)}</span>
                        <span className="dashboard__widget-pct">{v.pct.toFixed(1)}%</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Row 3: Recent Invoices + Spend by Category */}
          <div className="dashboard__widgets">
            {/* Widget 7: Recent Invoices */}
            <div className="card">
              <h3 className="dashboard__widget-title">Recent Invoices</h3>
              {recentInvoices.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon" aria-hidden="true">&#128196;</span>
                  <span className="empty-state__title">No invoices yet</span>
                </div>
              ) : (
                <ul className="dashboard__widget-list">
                  {recentInvoices.map((inv, i) => (
                    <li key={i} className="dashboard__widget-item" onClick={() => { setSelectedInvoiceId(inv.id); setShowDetailModal(true); }} style={{ cursor: 'pointer' }}>
                      <span className="dashboard__widget-item-name">#{inv.invoice_number || inv.id} {inv.vendor_name}</span>
                      <span className="dashboard__widget-item-detail">
                        <span className="dashboard__widget-date">{inv.invoice_date}</span>
                        <span className="td-amt">{formatCurrency(inv.total_amount)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="../invoices" className="dashboard__widget-link">View all invoices</Link>
            </div>

            {/* Widget 8: Spend by Category */}
            <div className="card">
              <h3 className="dashboard__widget-title">Spend by Category</h3>
              {topCategories.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon" aria-hidden="true">&#128193;</span>
                  <span className="empty-state__title">No category data</span>
                </div>
              ) : (
                <ul className="dashboard__widget-list">
                  {topCategories.map((cat, i) => (
                    <li key={i} className="dashboard__widget-item">
                      <span className="dashboard__widget-item-rank">{i + 1}</span>
                      <span className="dashboard__widget-item-name">{cat.category}</span>
                      <span className="dashboard__widget-item-detail">
                        <span className="td-amt">{formatCurrency(cat.total_spent)}</span>
                        <span className="dashboard__widget-pct">{cat.pct.toFixed(1)}%</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="../analytics" className="dashboard__widget-link">View all categories</Link>
            </div>
          </div>
        </>
      )}

      <InvoiceDetailModal
        invoiceId={selectedInvoiceId}
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedInvoiceId(null); }}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

Dashboard.propTypes = {
  /** Currently selected business ID from App header */
  activeBusiness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Dashboard;
